import { styled } from "styled-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { HiOutlineArrowUpRight } from "react-icons/hi2";

type Project = {
  id: string;
  title: string;
  description: string;
  category: string;
  summary: string;
  challenge: string;
  results: string[];
  accent: string;
  date: string;
  year: string;
  background: string;
  logo: string;
  url: string;
  logoWidth?: number;
};

const projects: Project[] = [
  {
    id: "kennyroyal",
    title: "Kenny Royal",
    category: "International Freight & Logistics",
    description: "International freight shipping company with a reputation for excellence, serving customers worldwide with reliable logistics solutions.",
    summary: "International freight shipping company with a reputation for excellence, serving customers worldwide with reliable logistics solutions.",
    challenge: "Build a professional digital presence that reflects their global reach while streamlining customer inquiries and quote requests.",
    results: ["Global service reach", "Streamlined operations", "Enhanced customer trust"],
    accent: "#1a5490",
    date: "September, 2023",
    year: "2023",
    background: require("../../assets/images/projects/kennyroyal/background.jpeg"),
    logo: require("../../assets/images/projects/kennyroyal/logo.png"),
    url: "https://kenny-royal-website.web.app/",
  },
  {
    id: "ced",
    title: "ced.",
    category: "Urban Logistics Platform",
    description: "Fast delivery service for food, documents and gadgets featuring real-time tracking, multi-order options, and doorstep pickup starting at GH₵ 10.00.",
    summary: "Dispatch automation, live courier tracking, and upsell-ready customer portals for a same-day delivery scale-up operating across Accra.",
    challenge: "We rebuilt the fulfilment workflow to remove manual dispatcher bottlenecks and give operations a real-time command centre.",
    results: ["45-minute average last-mile drop", "Fleet utilisation +28%", "Customer NPS 4.7/5"],
    accent: "#f7b733",
    date: "April, 2024",
    year: "2024",
    background: require("../../assets/images/projects/ced/background.jpg"),
    logo: require("../../assets/images/projects/ced/logo_min_dark.png"),
    url: "http://cedelivery.com/",
  },
  {
    id: "julitidytide",
    title: "Juli's Tidy Tide",
    category: "Hospitality & Facility Care",
    description: "Professional cleaning service specializing in eco-friendly practices for spotless and sustainable home and office environments.",
    summary: "Mobile-first experiences for eco-conscious cleaning services featuring transparent pricing, subscriptions, and crew routing.",
    challenge: "Help an expanding service brand deliver personalised packages and support a fast-growing partner network across new districts.",
    results: ["Self-service bookings +63%", "Repeat clients +41%", "Support calls -25%"],
    accent: "#4fc3f7",
    date: "April, 2025",
    year: "2025",
    background: require("../../assets/images/projects/julitidytide/background.jpg"),
    logo: require("../../assets/images/projects/julitidytide/logo.png"),
    url: "https://tidywithjuli.com/",
  },
  {
    id: "empai",
    title: "emp.ai",
    category: "EdTech & Assessment",
    description: "AI-powered learning and exam preparation platform for BECE/WASSCE students, offering guided practice, instant feedback, and adaptive mentorship.",
    summary: "Personalized exam readiness for students and schools, with AI-driven practice, feedback, and performance tracking.",
    challenge: "Create an engaging, data-driven platform that helps learners and schools prepare for exams while automating assessment workflows.",
    results: ["10k+ practice questions", "50+ schools reached", "24/7 AI study support"],
    accent: "#9c6bff",
    date: "August, 2025",
    year: "2025",
    background: require("../../assets/images/projects/empai/background.png"),
    logo: require("../../assets/images/projects/empai/logo.png"),
    url: "https://empai.space",
  },
  {
    id: "agate",
    title: "Agate Creative Studio",
    category: "Production & Media",
    description: "High-end photo and video production studio delivering cinematic-quality content for studio shoots, events, and custom creative projects.",
    summary: "An immersive booking and showcase experience capturing high-end visual storytelling and automating studio resourcing.",
    challenge: "Create a premium digital touchpoint that matches the studio's cinematic quality while simplifying shoot scheduling and approvals.",
    results: ["Showreel load time <1.2s", "Booking requests +62%", "Client review cycles -35%"],
    accent: "#ff6f61",
    date: "October, 2025",
    year: "2025",
    background: require("../../assets/images/projects/agate/background.jpg"),
    logo: require("../../assets/images/projects/agate/logo.png"),
    url: "https://agatestudios.com/",
    logoWidth: 80,
  },
];

const ProjectsSection = () => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const stageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const activeProject = projects[activeIndex];
  const [percentageVisible, setPercentageVisible] = useState<number>(0);
  const [timelineProgress, setTimelineProgress] = useState<number>(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      const mediaQuery = window.matchMedia("(max-width: 768px)");
      setIsMobile(mediaQuery.matches);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      setTimelineProgress(0);
      setPercentageVisible(0);
      return;
    }

    const handleScroll = () => {
      if (!sectionRef.current) {
        return;
      }

      const sectionRect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const visibleHeight =
        Math.min(sectionRect.bottom, windowHeight) -
        Math.max(sectionRect.top, 0);
      const percent = (visibleHeight / sectionRect.height) * 100;
      setPercentageVisible(percent);

      const progress = Math.max(
        0,
        Math.min(
          1,
          (windowHeight - sectionRect.top) / (windowHeight + sectionRect.height)
        )
      );
      setTimelineProgress(progress * 100);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const indexAttr = entry.target.getAttribute("data-index");
            if (!indexAttr) {
              return;
            }

            const index = Number(indexAttr);
            if (!Number.isNaN(index)) {
              setActiveIndex((current) => (current !== index ? index : current));
            }
          }
        });
      },
      {
        threshold: 0.6,
      }
    );

    const stages = stageRefs.current;

    stages.forEach((stage) => {
      if (stage) {
        observer.observe(stage);
      }
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      stages.forEach((stage) => {
        if (stage) {
          observer.unobserve(stage);
        }
      });
      observer.disconnect();
    };
  }, [isMobile]);

  const getRadius = (percentage: number) => {
    return 100 - (percentage / 100) * (isMobile ? 250 : 90) + "%";
  };

  const registerStage = useCallback(
    (index: number) => (element: HTMLDivElement | null) => {
      stageRefs.current[index] = element;
    },
    []
  );

  const scrollToStage = (index: number) => {
    const stage = stageRefs.current[index];
    if (stage) {
      stage.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <ProjectsSectionContainer
      ref={sectionRef}
      id="portfolio"
      style={{
        borderRadius: isMobile ? "0" : `0 0 0 ${getRadius(percentageVisible)}`,
      }}
    >
      {isMobile ? (
        <MobileSectionContent>
          <SectionHeader>
            <Title>Our Journey</Title>
            <Subtitle>Selected projects, quietly arranged</Subtitle>
          </SectionHeader>

          <MobileProjectList>
            {projects.map((project) => (
              <MobileProjectCard key={project.id}>
                <MobileProjectTopRow>
                  <MobileProjectDateGroup>
                    <TimelineYear>{project.year}</TimelineYear>
                    <TimelineDate>{project.date}</TimelineDate>
                  </MobileProjectDateGroup>
                  <ProjectLogo
                    src={project.logo}
                    alt={project.title}
                    style={{ width: project.logoWidth || 46 }}
                  />
                </MobileProjectTopRow>

                <MobileAccentLine style={{ background: project.accent }} />
                <Category>{project.category}</Category>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectSummary>{project.summary}</ProjectSummary>

                <ResultsList>
                  {project.results.map((result) => (
                    <ResultPill key={result} style={{ borderColor: project.accent }}>
                      {result}
                    </ResultPill>
                  ))}
                </ResultsList>

                <ViewProjectLink
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View live experience
                  <HiOutlineArrowUpRight />
                </ViewProjectLink>
              </MobileProjectCard>
            ))}
          </MobileProjectList>
        </MobileSectionContent>
      ) : (
        <>
          <StickyLayer>
            <ContentWrapper>
              <TimelineSection>
                <SectionHeader>
                  <Title>Our Journey</Title>
                  <Subtitle>Project Timeline</Subtitle>
                </SectionHeader>
                <MobileTimelineWrapper>
                  {projects.map((project, index) => (
                    <TimelineItem key={project.id} $active={index === activeIndex}>
                      <TimelineContent
                        $accent={project.accent}
                        onClick={() => scrollToStage(index)}
                      >
                        <TimelineYear>{project.year}</TimelineYear>
                        <TimelineDate>{project.date}</TimelineDate>
                        <TimelineTitle>{project.title}</TimelineTitle>
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </MobileTimelineWrapper>
                <TimelineTrack>
                  <TimelineFill progress={timelineProgress} />
                  {projects.map((project, index) => (
                    <TimelineItem key={project.id} $active={index === activeIndex}>
                      <TimelineContent
                        $accent={project.accent}
                        onClick={() => scrollToStage(index)}
                      >
                        <TimelineYear>{project.year}</TimelineYear>
                        <TimelineDate>{project.date}</TimelineDate>
                        <TimelineTitle>{project.title}</TimelineTitle>
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </TimelineTrack>
              </TimelineSection>

              <ProjectPreview>
                <PreviewCard key={activeProject.id} background={activeProject.background}>
                  <PreviewOverlay />
                  <AccentBar style={{ background: activeProject.accent }} />
                  <PreviewContent>
                    <ProjectMeta>
                      <Category>{activeProject.category}</Category>
                      <LogoContainer>
                        <ProjectLogo
                          src={activeProject.logo}
                          alt={activeProject.title}
                          style={{ width: activeProject.logoWidth || 50 }}
                        />
                      </LogoContainer>
                    </ProjectMeta>
                    <ProjectTitle>{activeProject.title}</ProjectTitle>
                    <ProjectSummary>{activeProject.summary}</ProjectSummary>
                    <ProjectChallenge>
                      <ChallengeLabel>What we unlocked</ChallengeLabel>
                      <ChallengeCopy>{activeProject.challenge}</ChallengeCopy>
                    </ProjectChallenge>
                    <ResultsList>
                      {activeProject.results.map((result) => (
                        <ResultPill
                          key={result}
                          style={{ borderColor: activeProject.accent }}
                        >
                          {result}
                        </ResultPill>
                      ))}
                    </ResultsList>
                    <ViewProjectLink
                      href={activeProject.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View live experience
                      <HiOutlineArrowUpRight />
                    </ViewProjectLink>
                  </PreviewContent>
                </PreviewCard>
              </ProjectPreview>
            </ContentWrapper>
          </StickyLayer>

          <ScrollArea aria-hidden="true">
            {projects.map((project, index) => (
              <ScrollStage
                key={project.id}
                data-index={index}
                ref={registerStage(index)}
              />
            ))}
          </ScrollArea>
        </>
      )}
    </ProjectsSectionContainer>
  );
};

const ProjectsSectionContainer = styled.div`
  position: relative;
  min-height: 100vh;
  background-color: #151515;
  color: white;
  border-bottom-left-radius: 30px;
  border-bottom-right-radius: 30px;
`;

const StickyLayer = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  align-items: stretch;
  padding: 6% 5%;
  background: #151515;
  z-index: 2;

  @media (max-width: 1100px) {
    position: relative;
    height: auto;
    padding: 10% 6%;
  }
`;

const ScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  pointer-events: none;
`;

const ScrollStage = styled.div`
  height: 100vh;
  background: rgba(255, 255, 255, 0.01);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  @media (max-width: 1100px) {
    height: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 60px;
  align-items: stretch;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const MobileSectionContent = styled.div`
  padding: 72px 20px 84px;
`;

const TimelineSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h2`
  color: white;
  margin: 0 0 10px 0;
  font-size: 32px;
  font-weight: 700;
`;

const Subtitle = styled.h3`
  color: #888888;
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const TimelineTrack = styled.div`
  position: relative;
  padding-left: 40px;

  &::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, #ffffff20, #ffffff10);
  }

  @media (max-width: 1100px) {
    display: none;
  }
`;

const TimelineFill = styled.div<{ progress: number }>`
  position: absolute;
  left: 8px;
  top: 0;
  width: 4px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.5));
  height: ${props => props.progress}%;
  transition: height 0.15s ease;
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.35);
  pointer-events: none;

  @media (max-width: 1100px) {
    display: none;
  }
`;

const TimelineItem = styled.div<{ $active: boolean }>`
  position: relative;
  padding: 20px 0;
  transition: all 0.3s ease;
  opacity: ${props => (props.$active ? 1 : 0.6)};

  @media (max-width: 1100px) {
    flex: 0 0 260px;
    min-width: 260px;
    padding: 18px;
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.05);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
    opacity: ${props => (props.$active ? 1 : 0.85)};
    transform: ${props => (props.$active ? "translateY(-2px)" : "none")};
    scroll-snap-align: start;
  }
`;

const TimelineDate = styled.div`
  font-size: 10px;
  color: #888888;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: color 0.15s ease;
`;

const TimelineContent = styled.div<{ $accent: string }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;

  &:hover ${TimelineDate} {
    color: ${props => props.$accent};
  }

  @media (max-width: 1100px) {
    gap: 8px;
  }
`;

const TimelineYear = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: white;
`;

const TimelineTitle = styled.div`
  font-size: 13px;
  color: white;
  margin-top: 4px;
`;

const ProjectPreview = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 100%;
`;

const MobileProjectList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 28px;
`;

const MobileProjectCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 20px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const MobileProjectTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const MobileProjectDateGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MobileAccentLine = styled.span`
  width: 56px;
  height: 3px;
  border-radius: 999px;
`;

const MobileTimelineWrapper = styled.div`
  display: none;

  @media (max-width: 1100px) {
    display: flex;
    overflow-x: auto;
    gap: 16px;
    padding-bottom: 10px;
    margin: 20px 0 0;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 999px;
    }
  }
`;

const PreviewCard = styled.div<{ background: string }>`
  position: relative;
  width: 100%;
  height: 600px;
  border-radius: 24px;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4)),
    url(${props => props.background}) center / cover no-repeat;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
  animation: fadeIn 0.6s ease;

  @media (max-width: 1100px) {
    min-height: 520px;
  }

  @media (max-width: 768px) {
    min-height: 460px;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const PreviewOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.6));
`;

const AccentBar = styled.span`
  position: absolute;
  height: 80px;
  width: 6px;
  top: 40px;
  left: 36px;
  border-radius: 999px;
`;

const PreviewContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 48px 48px 52px 64px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 768px) {
    padding: 38px 28px 42px 48px;
  }
`;

const ProjectMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
`;

const Category = styled.span`
  font-size: 13px;
  letter-spacing: 2.4px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
`;

const LogoContainer = styled.div`
  margin-bottom: 10px;
`;

const ProjectLogo = styled.img`
  height: auto;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  padding: 10px;
`;

const ProjectTitle = styled.h3`
  margin: 0;
  font-size: 34px;
  line-height: 1.2;
  color: white;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const ProjectSummary = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.78);
`;

const ProjectChallenge = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
`;

const ChallengeLabel = styled.span`
  font-size: 11px;
  letter-spacing: 2.4px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
`;

const ChallengeCopy = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.82);
`;

const ResultsList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0;
  margin: 4px 0 0 0;
  list-style: none;
`;

const ResultPill = styled.li`
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 12px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: white;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(6px);
`;

const ViewProjectLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 40px;
  font-size: 13px;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-decoration: none;
  color: white;
`;

export default ProjectsSection;
